import {
  ViewFolderId,
  ViewId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { ViewColumnsConfig } from "@redotech/effect-api-shared/rpc/view-folder";
import type { SupportTicketSort } from "@redotech/effect-api-shared/schema/query-options";
import { useHandler } from "@redotech/react-util/hook";
import type { FilterGroup } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { useSupportViewsFoldersCollections } from "../providers/support-views-folders-provider";

// Historical persisted view/folder table id. New support-v2 code should use
// support ticket terminology around this value, but changing the value itself
// requires a view/folder migration.
const LEGACY_SUPPORT_TICKET_VIEW_TABLE = "support-v2-conversations" as const;

// Readonly Effect Schema columns can't be written into a TanStack DB
// collection row (its update callback exposes a mutable Immer proxy), so
// clone into plain mutable structures before insert/update.
function cloneColumnsForMutation(
  columns: ViewColumnsConfig | null,
): { order: string[] | null; visibility: Record<string, boolean> } | null {
  if (columns === null) {
    return null;
  }
  return {
    order: columns.order ? [...columns.order] : null,
    visibility: { ...columns.visibility },
  };
}

// Readonly Effect Schema sort arrays can't be written into a TanStack DB
// collection row (its update callback exposes a mutable Immer proxy), so clone
// into plain mutable structures before insert/update.
function cloneSortForMutation(
  sort: readonly SupportTicketSort[] | null,
): SupportTicketSort[] | null {
  if (sort === null || sort.length === 0) {
    return null;
  }
  return sort.map((s) => ({ ...s }));
}

interface ViewFolderInfo {
  id: string;
  folderId: string | null;
}

export function useSupportV2FolderActions(): {
  createFolder: (
    name: string,
    viewIds: string[],
    isPrivate: boolean,
    defaultViews: string[],
  ) => Promise<void>;
  updateFolder: (
    folderId: string,
    name: string,
    viewIds: string[],
    allViews: ViewFolderInfo[],
    defaultViews: string[],
  ) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  createView: (
    name: string,
    filterGroup: FilterGroup | null,
    folderId: string | null,
    columns: ViewColumnsConfig | null,
    sort: readonly SupportTicketSort[] | null,
  ) => Promise<ViewId>;
  updateView: (
    viewId: ViewId,
    name: string,
    filterGroup: FilterGroup | null,
    columns: ViewColumnsConfig | null,
    sort: readonly SupportTicketSort[] | null,
  ) => Promise<void>;
  updateViewColumns: (
    viewId: ViewId,
    columns: ViewColumnsConfig | null,
  ) => Promise<void>;
  deleteView: (viewId: ViewId) => Promise<void>;
} {
  const collections = useSupportViewsFoldersCollections();

  const createView = useHandler(
    async (
      name: string,
      filterGroup: FilterGroup | null,
      folderId: string | null,
      columns: ViewColumnsConfig | null,
      sort: readonly SupportTicketSort[] | null,
    ): Promise<ViewId> => {
      const viewId = ViewId.generate();
      collections.views.insert({
        id: viewId,
        name,
        table: LEGACY_SUPPORT_TICKET_VIEW_TABLE,
        filters: [],
        filterGroup,
        columns: cloneColumnsForMutation(columns),
        sort: cloneSortForMutation(sort),
        pageSize: null,
        folderId: folderId ? ViewFolderId.make(folderId) : null,
        position: 0,
        deleted: false,
        isNotEditable: false,
      });
      return viewId;
    },
  );

  const updateView = useHandler(
    async (
      viewId: ViewId,
      name: string,
      filterGroup: FilterGroup | null,
      columns: ViewColumnsConfig | null,
      sort: readonly SupportTicketSort[] | null,
    ): Promise<void> => {
      collections.views.update(viewId, (draft) => {
        draft.name = name;
        draft.filterGroup = filterGroup;
        draft.columns = cloneColumnsForMutation(columns);
        draft.sort = cloneSortForMutation(sort);
      });
    },
  );

  const updateViewColumns = useHandler(
    async (
      viewId: ViewId,
      columns: ViewColumnsConfig | null,
    ): Promise<void> => {
      collections.views.update(viewId, (draft) => {
        draft.columns = cloneColumnsForMutation(columns);
      });
    },
  );

  const deleteView = useHandler(async (viewId: ViewId): Promise<void> => {
    collections.views.delete(viewId);
  });

  const createFolder = useHandler(
    async (
      name: string,
      viewIds: string[],
      isPrivate: boolean,
      defaultViews: string[],
    ) => {
      const folderId = ViewFolderId.generate();
      collections.folders.insert({
        id: folderId,
        name,
        table: LEGACY_SUPPORT_TICKET_VIEW_TABLE,
        position: 0,
        isPrivate,
        defaultViews: [...defaultViews],
      });
      // Assign custom views to the new folder
      for (const viewId of viewIds) {
        collections.views.update(ViewId.make(viewId), (draft) => {
          draft.folderId = folderId;
        });
      }
    },
  );

  const updateFolder = useHandler(
    async (
      folderId: string,
      name: string,
      viewIds: string[],
      allViews: ViewFolderInfo[],
      defaultViews: string[],
    ) => {
      const folderIdTyped = ViewFolderId.make(folderId);
      collections.folders.update(folderIdTyped, (draft) => {
        draft.name = name;
        draft.defaultViews = [...defaultViews];
      });
      // Update view assignments: add folderId to selected views, remove from unselected
      const selectedSet = new Set(viewIds);
      for (const view of allViews) {
        const isSelected = selectedSet.has(view.id);
        const currentlyInThisFolder = view.folderId === folderId;

        if (isSelected && !currentlyInThisFolder) {
          // Add to this folder
          collections.views.update(ViewId.make(view.id), (draft) => {
            draft.folderId = folderIdTyped;
          });
        } else if (!isSelected && currentlyInThisFolder) {
          // Remove from this folder
          collections.views.update(ViewId.make(view.id), (draft) => {
            draft.folderId = null;
          });
        }
      }
    },
  );

  const deleteFolder = useHandler(async (folderId: string) => {
    collections.folders.delete(ViewFolderId.make(folderId));
  });

  return {
    createView,
    updateView,
    updateViewColumns,
    deleteView,
    createFolder,
    updateFolder,
    deleteFolder,
  };
}
